import {
  canReleaseLocalPrintIntent,
  localPrintNeedsPrinterCheck,
  releaseLocalPrintIntent,
} from '@/lib/local-print'
import { canRetryDirectPrintJob, retryDirectPrintJob } from '@/lib/local-print-direct-jobs'
import { stackToastAction } from '@/components/ui/toast'
import { readProperty } from '@/lib/utils'
import { toast, type ExternalToast } from 'sonner'

export function localPrintToastOptions(
  error: unknown,
  description: string | undefined,
  onIntentReleased?: () => void
): ExternalToast {
  const canRetryDurableJob = canRetryDirectPrintJob(error)
  const allowAnotherPrint = async () => {
    if (canRetryDurableJob) {
      try {
        await retryDirectPrintJob(error)
        toast.success('Sent to printer.')
      } catch (retryError) {
        const needsPrinterCheck = localPrintNeedsPrinterCheck(retryError)
        const retryMessage = readProperty(retryError, 'message')
        toast[needsPrinterCheck ? 'warning' : 'error'](
          needsPrinterCheck
            ? 'Check the printer before printing again'
            : 'Could not retry the print',
          localPrintToastOptions(
            retryError,
            (typeof retryMessage === 'string' && retryMessage) || 'Try again.',
            onIntentReleased
          )
        )
      }
      return
    }

    if (!releaseLocalPrintIntent(error)) {
      toast.error('Could not release the earlier print. Try again or contact support.')
      return
    }

    onIntentReleased?.()
    toast.success('You can print again.')
  }
  const action =
    canRetryDurableJob || canReleaseLocalPrintIntent(error)
      ? {
          label: 'Allow another print',
          onClick: () => {
            void allowAnotherPrint()
          },
        }
      : undefined

  const options: ExternalToast = {
    description,
    ...(action ? { duration: Infinity, action } : {}),
  }

  return action ? stackToastAction(options) : options
}
